/**
 * Content Pipeline
 * 
 * Unified dashboard for scanning agents and admins.
 * This is THE single interface for the entire scan → edit → publish workflow.
 * 
 * Features:
 * - "My Active Work" - assigned book_scan_requests (parent-initiated)
 * - "My Books" - scanned_books needing attention (proactive scans, failed extractions)
 * - "Available to Claim" - pending parent requests
 * - "+ Add New Book" - proactive scanning (no parent request needed)
 * - "Inputs" - raw data inspection for debugging
 * - Device-aware UI (scan on tablet, edit on desktop)
 * - Real-time updates when status changes from another device
 * 
 * Architecture note: We deliberately avoid Kanban-style columns.
 * Agents work on ONE book at a time sequentially, not parallel tasks.
 * The task-list pattern (My Work → My Books → Available → Add New) fits this better.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  BookOpen, 
  Clock, 
  User, 
  RefreshCw,
  AlertCircle,
  Camera,
  Edit3,
  Loader2,
  Trash2,
  AlertTriangle,
} from 'lucide-react';
import { glassStyles } from '@/lib/design';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase/client';
import {
  getPendingScanRequests,
  assignScanRequest,
  deleteScanRequest,
  reuseBookContent,
  type BookScanRequest,
} from '@/services/scan';
import { MyActiveWork } from '@/components/scan-agent/MyActiveWork';
import { MyInProgressBooks } from '@/components/scan-agent/MyInProgressBooks';
import { detectDeviceType, type DeviceType } from '@/lib/device';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from 'sonner';

interface ExistingBookMatch {
  id: string;
  status: string;
  binding: string | null;
  matchType: 'isbn' | 'work_group';
}

interface RequestWithProfile extends BookScanRequest {
  requester_name?: string;
  assignee_name?: string;
  existing_match?: ExistingBookMatch;
}

// ============================================================================
// Component
// ============================================================================

export function ScanAgentQueue() {
  const { profile, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  const [requests, setRequests] = useState<RequestWithProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [claimingId, setClaimingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [reusingId, setReusingId] = useState<string | null>(null);
  const [currentDevice] = useState<DeviceType>(detectDeviceType);

  // ============================================================================
  // Data Fetching
  // ============================================================================

  const fetchRequests = useCallback(async () => {
    try {
      const data = await getPendingScanRequests();
      
      // Fetch profile names for requesters and assignees
      const profileIds = new Set<string>();
      data.forEach(r => {
        profileIds.add(r.requested_by);
        if (r.assigned_to) profileIds.add(r.assigned_to);
      });

      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, name')
        .in('id', Array.from(profileIds));

      const profileMap = new Map(
        (profiles || []).map(p => [p.id, p.name])
      );

      // Cross-reference against global library for duplicates + work_group matches
      const { data: existingBooks } = await supabase
        .from('scanned_books')
        .select('id, isbn, quality_status, binding, work_group');

      const isbnMap = new Map<string, { id: string; status: string; binding: string | null }>();
      const workGroupMap = new Map<string, { id: string; status: string; binding: string | null }>();
      for (const book of existingBooks || []) {
        const entry = { id: book.id, status: book.quality_status || 'processing', binding: book.binding };
        if (book.isbn) isbnMap.set(book.isbn, entry);
        if (book.work_group) {
          const existing = workGroupMap.get(book.work_group);
          if (!existing || book.quality_status === 'ready') {
            workGroupMap.set(book.work_group, entry);
          }
        }
      }

      const enrichedData: RequestWithProfile[] = data.map(r => {
        // Exact ISBN match takes priority
        const byIsbn = r.isbn ? isbnMap.get(r.isbn) : undefined;
        if (byIsbn) {
          return {
            ...r,
            requester_name: profileMap.get(r.requested_by) || 'Unknown',
            assignee_name: r.assigned_to ? profileMap.get(r.assigned_to) || 'Unknown' : undefined,
            existing_match: { ...byIsbn, matchType: 'isbn' as const },
          };
        }

        // Work group match (same work, different edition)
        const byWorkGroup = r.work_group ? workGroupMap.get(r.work_group) : undefined;
        return {
          ...r,
          requester_name: profileMap.get(r.requested_by) || 'Unknown',
          assignee_name: r.assigned_to ? profileMap.get(r.assigned_to) || 'Unknown' : undefined,
          existing_match: byWorkGroup ? { ...byWorkGroup, matchType: 'work_group' as const } : undefined,
        };
      });

      setRequests(enrichedData);
      setError(null);
    } catch (err) {
      console.error('Error fetching requests:', err);
      setError('Failed to load requests');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Poll every 30 seconds to keep the queue fresh
  useEffect(() => {
    const interval = setInterval(() => {
      fetchRequests();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchRequests]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchRequests();
  };

  // ============================================================================
  // Actions
  // ============================================================================

  const handleClaimRequest = async (requestId: string) => {
    if (!profile?.id) return;
    
    setClaimingId(requestId);
    try {
      await assignScanRequest(requestId, profile.id);
      await fetchRequests();
    } catch (err) {
      console.error('Error claiming request:', err);
      setError('Failed to claim request');
    } finally {
      setClaimingId(null);
    }
  };

  const handleDeleteRequest = async (requestId: string) => {
    setDeletingId(requestId);
    try {
      await deleteScanRequest(requestId);
      toast.success('Request deleted');
      await fetchRequests();
    } catch (err) {
      console.error('Error deleting request:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to delete request');
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  const handleReuseContent = async (requestId: string, sourceBookId: string) => {
    setReusingId(requestId);
    try {
      await reuseBookContent(sourceBookId, requestId);
      toast.success('Edition created — pending turn code review');
      await fetchRequests();
    } catch (err) {
      console.error('Error reusing content:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to reuse content');
    } finally {
      setReusingId(null);
    }
  };

  const handleStartScanning = async (request: RequestWithProfile) => {
    // Update device type before navigating
    await supabase
      .from('book_scan_requests')
      .update({ last_device_type: currentDevice })
      .eq('id', request.id);
    navigate(`/scan-agent/scan/request/${request.id}`);
  };

  const handleStartEditing = async (request: RequestWithProfile) => {
    if (!request.resolved_book_id) return;
    // Update device type before navigating
    await supabase
      .from('book_scan_requests')
      .update({ last_device_type: currentDevice })
      .eq('id', request.id);
    navigate(`/scan-agent/edit/${request.resolved_book_id}`);
  };

  // ============================================================================
  // Render
  // ============================================================================

  const getStatusBadge = (status: string) => {
    const styles: Record<string, { bg: string; text: string; label: string }> = {
      pending: { bg: 'bg-[#FF9F0A]/15', text: 'text-[#FF9F0A]', label: 'Pending' },
      assigned: { bg: 'bg-[#0A84FF]/15', text: 'text-[#0A84FF]', label: 'Assigned' },
      scanning: { bg: 'bg-[#5E5CE6]/15', text: 'text-[#5E5CE6]', label: 'Scanning' },
      editing: { bg: 'bg-[#30D158]/15', text: 'text-[#30D158]', label: 'Editing' },
      ready: { bg: 'bg-[#30D158]/15', text: 'text-[#30D158]', label: 'Ready' },
    };
    const style = styles[status] || styles.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
        {style.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-[#0A84FF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* My Active Work - Shows assigned tasks (from book_scan_requests) */}
      <MyActiveWork onRefresh={fetchRequests} />

      {/* My In-Progress Books - Shows scanned_books needing attention (proactive scans) */}
      <MyInProgressBooks onRefresh={fetchRequests} />

      {/* Header - clean, minimal */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#1C1C1E]">Available Requests</h1>
          <p className="text-sm text-[#8E8E93]">
            {requests.length} request{requests.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-black/[0.03] text-[#636366] hover:bg-black/[0.05] transition-all disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 rounded-xl bg-[#FF453A]/10 border border-[#FF453A]/20 text-[#FF453A] flex items-center gap-3">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Request List - simplified: no cover images, compact metadata */}
      {requests.length === 0 ? (
        <div className="text-center py-12">
          <BookOpen className="w-12 h-12 text-[#AEAEB2] mx-auto mb-4" />
          <div className="text-[#8E8E93]">No requests to show</div>
        </div>
      ) : (
        <div className="space-y-2">
          {requests.map((request) => (
            <div
              key={request.id}
              className="px-4 py-3 rounded-2xl transition-all hover:brightness-110"
              style={glassStyles.card}
            >
              <div className="flex items-center gap-4">
                {/* Info - compact */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <h3 className="font-medium text-[#1C1C1E] truncate">{request.title}</h3>
                    {getStatusBadge(request.status)}
                    {request.binding && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-black/[0.05] text-[#636366]">
                        {request.binding}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-[#8E8E93]">
                    {request.author && <span>{request.author}</span>}
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {request.requester_name}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(request.created_at).toLocaleDateString()}
                    </span>
                    {request.assignee_name && (
                      <span className="text-[#0A84FF]">
                        → {request.assignee_name}
                      </span>
                    )}
                    {request.existing_match?.matchType === 'isbn' && (
                      <Link
                        to={`/scan-agent/edit/${request.existing_match.id}`}
                        className="flex items-center gap-1 text-[#FF9F0A] hover:text-[#FF9F0A]/80 transition-colors"
                      >
                        <AlertTriangle className="w-3 h-3" />
                        Already in library ({request.existing_match.status})
                      </Link>
                    )}
                    {request.existing_match?.matchType === 'work_group' && (
                      <Link
                        to={`/scan-agent/edit/${request.existing_match.id}`}
                        className="flex items-center gap-1 text-[#BF5AF2] hover:text-[#BF5AF2]/80 transition-colors"
                      >
                        <BookOpen className="w-3 h-3" />
                        Same work exists{request.existing_match.binding ? ` as ${request.existing_match.binding}` : ''} ({request.existing_match.status})
                      </Link>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {request.status === 'pending' && request.existing_match?.matchType === 'work_group' && (request.existing_match.status === 'ready' || request.existing_match.status === 'turn_codes_pending') && (
                    <button
                      onClick={() => handleReuseContent(request.id, request.existing_match!.id)}
                      disabled={reusingId === request.id}
                      className="px-3 py-2 rounded-lg bg-[#BF5AF2] text-white text-sm font-medium transition-all hover:brightness-110 disabled:opacity-50"
                    >
                      {reusingId === request.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Reuse Content'
                      )}
                    </button>
                  )}
                  {request.status === 'pending' && (
                    <button
                      onClick={() => handleClaimRequest(request.id)}
                      disabled={claimingId === request.id}
                      className="px-3 py-2 rounded-lg bg-[#0A84FF] text-white text-sm font-medium transition-all hover:brightness-110 disabled:opacity-50"
                    >
                      {claimingId === request.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Claim'
                      )}
                    </button>
                  )}
                  
                  {(request.status === 'assigned' || request.status === 'scanning') && 
                   request.assigned_to === profile?.id && (
                    <button
                      onClick={() => handleStartScanning(request)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#5E5CE6] text-white text-sm font-medium transition-all hover:brightness-110"
                    >
                      <Camera className="w-4 h-4" />
                      Scan
                    </button>
                  )}
                  
                  {request.status === 'editing' && request.resolved_book_id && (
                    <button
                      onClick={() => handleStartEditing(request)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#30D158] text-white text-sm font-medium transition-all hover:brightness-110"
                    >
                      <Edit3 className="w-4 h-4" />
                      Edit
                    </button>
                  )}
                  
                  {/* Delete button for pending/assigned requests (admins + scan agents) */}
                  {(isAdmin() || request.status === 'pending' || request.status === 'assigned') && (
                    <button
                      onClick={() => setConfirmDeleteId(request.id)}
                      disabled={deletingId === request.id}
                      className="p-2 rounded-lg text-[#8E8E93] hover:text-[#FF453A] hover:bg-[#FF453A]/10 transition-all disabled:opacity-50"
                      title="Delete request"
                    >
                      {deletingId === request.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={() => confirmDeleteId && handleDeleteRequest(confirmDeleteId)}
        title="Delete scan request"
        message="Are you sure you want to delete this scan request? This action cannot be undone."
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  );
}

export default ScanAgentQueue;
